import { errorMessage } from '../lib/errorMessage';
import type { LiveStatus } from '../api/sse';

// Left-aligned (no `justify-center`) so the leading icon stays anchored
// while the trailing text width changes — keeps the ring/spinner from
// shifting horizontally as the status label grows or shrinks.
const PILL_BASE =
  'inline-flex w-36 items-center gap-2 rounded border px-3 py-1.5 text-sm font-semibold';
const PILL_FETCHING = `${PILL_BASE} border-slate-300 bg-white text-slate-500`;
const PILL_PAUSED = `${PILL_BASE} border-slate-200 bg-slate-50 text-slate-400`;
const PILL_ERROR = `${PILL_BASE} border-red-300 bg-red-50 text-red-700 hover:border-red-400`;
const PILL_LIVE = `${PILL_BASE} border-emerald-300 bg-emerald-50 text-emerald-700`;
const PILL_ENDED = `${PILL_BASE} border-slate-300 bg-slate-100 text-slate-600`;

// The pill renders the live SSE subscription status: no fixed cadence, so no
// countdown — just connection liveness (live | paused | reconnecting | error |
// ended). The `kind: 'sse'` discriminant is retained so call sites read
// self-documentingly at the use site.
export type RefreshPillProps = { kind: 'sse'; status: LiveStatus; error: Error | null };

export function RefreshPill(props: RefreshPillProps) {
  return <SsePill status={props.status} error={props.error} />;
}

function SsePill({ status, error }: { status: LiveStatus; error: Error | null }) {
  if (status === 'error') {
    return (
      <span className={PILL_ERROR} title={error ? errorMessage(error) : undefined}>
        <span aria-hidden>✕</span>
        <span>Error</span>
      </span>
    );
  }
  if (status === 'paused') {
    return (
      <span className={PILL_PAUSED}>
        <span aria-hidden>•</span>
        <span>Paused</span>
      </span>
    );
  }
  if (status === 'reconnecting') {
    return (
      <span className={PILL_FETCHING}>
        <Spinner />
        <span>Reconnecting…</span>
      </span>
    );
  }
  if (status === 'ended') {
    return (
      <span className={PILL_ENDED}>
        <span aria-hidden className="inline-block h-2 w-2 shrink-0 rounded-full bg-slate-400" />
        <span>Ended</span>
      </span>
    );
  }
  return (
    <span className={PILL_LIVE}>
      <span aria-hidden className="inline-block h-2 w-2 shrink-0 rounded-full bg-emerald-500" />
      <span>Live</span>
    </span>
  );
}

function Spinner() {
  return (
    <span
      aria-hidden
      className="inline-block h-3 w-3 animate-spin rounded-full border-2 border-slate-300 border-t-slate-500"
    />
  );
}
